import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@material-ui/core";
import MaterialTable, { Column } from "material-table";
import BreadcrumbNav from "../../../../components/BreadcrumbNav/BreadcrumbNav";
import { materialTableES } from "../../../../services/materialTableES";
import { errorToast, successToast } from "../../../../services/toasts";
import { AdminSupplier, SupplierFeature } from "../../../../types/featureFlags";
import styles from "./FeatureFlagsManagement.module.scss";
import {
  useGetAdminSuppliers,
  useGetSupplierFeatures,
  useUpdateSupplierFeature,
} from "../../../../utils/react-query/queryFeatureFlags/queries";

const getErrorMessage = (error: unknown, fallbackMessage: string) => {
  return error instanceof Error ? error.message : fallbackMessage;
};

const FeatureFlagsManagement = () => {
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | "">("");

  const { data: suppliers = [], isLoading: isLoadingSuppliers } =
    useGetAdminSuppliers();
  const { data: supplierFeatures = [], isLoading: isLoadingFeatures } =
    useGetSupplierFeatures(
      selectedSupplierId ? Number(selectedSupplierId) : undefined
    );
  const { mutateAsync: updateSupplierFeature, isLoading: isUpdatingFeature } =
    useUpdateSupplierFeature();

  useEffect(() => {
    if (!selectedSupplierId && suppliers.length > 0) {
      setSelectedSupplierId(suppliers[0].supplier_id);
    }
  }, [selectedSupplierId, suppliers]);

  const selectedSupplier = useMemo<AdminSupplier | undefined>(() => {
    return suppliers.find(
      (supplier) => supplier.supplier_id === Number(selectedSupplierId)
    );
  }, [selectedSupplierId, suppliers]);

  const summary = useMemo(() => {
    const enabledCount = supplierFeatures.filter(
      (feature) => feature.is_enabled
    ).length;
    const inactiveCount = supplierFeatures.length - enabledCount;

    return {
      total: supplierFeatures.length,
      enabled: enabledCount,
      inactive: inactiveCount,
    };
  }, [supplierFeatures]);

  const handleToggleFeature = async (feature: SupplierFeature) => {
    if (!selectedSupplierId) {
      return;
    }

    try {
      await updateSupplierFeature({
        supplierId: Number(selectedSupplierId),
        featureId: feature.id,
        is_enabled: !feature.is_enabled,
      });

      successToast(
        `El feature flag ${feature.name} fue ${
          feature.is_enabled ? "deshabilitado" : "habilitado"
        }`
      );
    } catch (error) {
      errorToast(
        getErrorMessage(error, "No fue posible actualizar el feature flag")
      );
    }
  };

  const columns: Column<SupplierFeature>[] = [
    {
      title: "Módulo",
      field: "name",
      render: (rowData) => (
        <div>
          <Typography className={styles.companyName}>{rowData.name}</Typography>
        </div>
      ),
    },
    {
      title: "Descripción",
      field: "description",
      render: (rowData) => rowData.description || "-",
    },
    {
      title: "Estado",
      field: "is_enabled",
      filtering: false,
      render: (rowData) => (
        <Box display="flex" alignItems="center" gridGap={8}>
          <Switch
            color="primary"
            checked={rowData.is_enabled}
            disabled={isUpdatingFeature}
            onChange={() => handleToggleFeature(rowData)}
          />
          <Chip
            size="small"
            className={
              rowData.is_enabled ? styles.enabledChip : styles.disabledChip
            }
            label={rowData.is_enabled ? "Encendido" : "Apagado"}
          />
        </Box>
      ),
    },
  ];

  return (
    <section className="layout-dashboard-container">
      <div className="header-container">
        <Box className={styles.headerRow}>
          <div>
            <h4 className="header-title">Feature Flags</h4>
            <BreadcrumbNav
              firstText="Admin"
              secondText="Feature Flags"
              secondLink="/admin/feature-flags"
              thirdText="Gestión"
            />
          </div>
        </Box>
      </div>

      <Paper className={styles.selectorCard} elevation={0}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={5}>
            <TextField
              select
              fullWidth
              variant="outlined"
              label="Proveedor"
              value={selectedSupplierId}
              onChange={(event) =>
                setSelectedSupplierId(Number(event.target.value))
              }
              disabled={isLoadingSuppliers || suppliers.length === 0}
            >
              {suppliers.map((supplier) => (
                <MenuItem
                  key={supplier.supplier_id}
                  value={supplier.supplier_id}
                >
                  {supplier.company_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={7}>
            <Typography className={styles.companyName}>
              {selectedSupplier?.company_name || "Selecciona un proveedor"}
            </Typography>
            <Typography className={styles.companyMeta}>
              {selectedSupplier
                ? `${selectedSupplier.email} ${
                    selectedSupplier.city ? `• ${selectedSupplier.city}` : ""
                  }`
                : "Todos los feature flags inician inactivos y se activan manualmente por proveedor."}
            </Typography>
            <div className={styles.helperBox}>
              <Typography variant="body2">
                Activa manualmente los flags necesarios para cada proveedor.
              </Typography>
            </div>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={2} className={styles.summaryGrid}>
        {[
          { label: "Total features", value: summary.total },
          { label: "Activos", value: summary.enabled },
          { label: "Inactivos", value: summary.inactive },
        ].map((item) => (
          <Grid item xs={12} sm={4} key={item.label}>
            <Paper className={styles.summaryCard} elevation={0}>
              <Typography className={styles.summaryLabel}>
                {item.label}
              </Typography>
              <Typography className={styles.summaryValue}>
                {item.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <div className={styles.modulesTableSection}>
        <MaterialTable
          title=""
          columns={columns}
          data={selectedSupplierId ? supplierFeatures : []}
          isLoading={isLoadingFeatures}
          localization={materialTableES}
          options={{
            search: true,
            paging: false,
            filtering: false,
            toolbar: true,
            headerStyle: {
              backgroundColor: "#f4f6f8",
              color: "#637381",
              fontSize: "14px",
              fontWeight: 600,
            },
            rowStyle: {
              fontSize: "14px",
              color: "#1C252E",
            },
            searchFieldVariant: "standard",
          }}
        />
      </div>
    </section>
  );
};

export default FeatureFlagsManagement;
